import * as XLSX from 'xlsx';
import axios from 'axios';

// Function to process Excel file and extract contacts
export const processExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Check if data exists
        if (jsonData.length < 2) {
          throw new Error('Le fichier Excel est vide ou ne contient pas suffisamment de données.');
        }
        
        // Extract headers (first row)
        const headers = jsonData[0].map(header => header.toLowerCase().trim());
        
        // Check required columns
        const requiredColumns = ['prénom', 'nom', 'téléphone'];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          throw new Error(`Colonnes requises manquantes: ${missingColumns.join(', ')}.`);
        }
        
        // Get column indexes
        const firstNameIndex = headers.indexOf('prénom');
        const lastNameIndex = headers.indexOf('nom');
        const phoneIndex = headers.indexOf('téléphone');
        
        // Process data rows (skip header)
        const contacts = jsonData.slice(1).map((row, index) => {
          // Skip empty rows
          if (!row.length || row.every(cell => !cell)) {
            return null;
          }
          
          // Extract data
          const firstName = row[firstNameIndex] || '';
          const lastName = row[lastNameIndex] || '';
          const phone = row[phoneIndex] ? String(row[phoneIndex]).trim() : '';
          
          return {
            id: index,
            firstName,
            lastName,
            phone,
            isValid: Boolean(firstName && lastName && phone)
          };
        }).filter(Boolean); // Remove null entries
        
        if (contacts.length === 0) {
          throw new Error('Aucun contact valide n\'a été trouvé dans le fichier.');
        }
        
        resolve(contacts);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Une erreur est survenue lors de la lecture du fichier.'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// Function to validate phone number format
export const validatePhoneNumber = (phone) => {
  // Tunisian phone number validation (both with and without country code)
  // Should handle formats like: +216 XX XXX XXX, 216XXXXXXXX, 2XXXXXXX, etc.
  
  // First, remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // If it has the country code (216)
  if (digits.startsWith('216')) {
    return digits.length === 11 && /^216[2459]\d{7}$/.test(digits);
  }
  
  // Without country code, should be 8 digits starting with 2, 4, 5, or 9
  return digits.length === 8 && /^[2459]\d{7}$/.test(digits);
};

// Function to format phone numbers consistently
export const formatPhoneNumber = (phone) => {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // If it has country code
  if (digits.startsWith('216') && digits.length === 11) {
    return `+216 ${digits.substring(3, 5)} ${digits.substring(5, 8)} ${digits.substring(8)}`;
  }
  
  // Without country code
  if (digits.length === 8) {
    return `+216 ${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5)}`;
  }
  
  // Return as is if it doesn't match expected patterns
  return phone;
};

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Fetch existing clients from the system
 * @returns {Promise} API response with client data
 */
export const fetchExistingClients = async () => {
  try {
    const response = await axios.get(`${API_URL}/sendsms/client`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    // Transform the client data to match the contact structure
    const formattedClients = response.data.map(client => ({
      firstName: client.clientFirstName || '',
      lastName: client.clientLastName || '',
      phone: client.clientNumber || ''
    }));
    
    return {
      success: true,
      data: formattedClients,
      message: `${formattedClients.length} clients récupérés avec succès.`
    };
  } catch (error) {
    console.error('Error fetching clients:', error);
    
    return {
      success: false,
      error: error,
      message: error.response?.data?.message || 
               error.message || 
               'Erreur lors de la récupération des clients.'
    };
  }
};